import React, { useMemo, useCallback } from "react";
import { isAfter, isValid, parseISO } from "date-fns";
import { Search } from "lucide-react";
import ExpandCollapse from "./conditions/ExpandCollapse";
import { useAdvanceSearch } from "../hooks/useAdvanceSearch";
import type { AdvanceSearchProps } from "../types";
import { AdvanceSearchRow } from "./conditions/AdvanceSearchRow";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/button";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { getBuilder } from "../builder";

const AdvanceSearch: React.FC<AdvanceSearchProps> = ({
  fields,
  portalId,
  iconColor = "#ffffff",
  limitRows = 4,
  onSearch,
  onClear,
  shortDateFormat
}) => {
  const fieldsData = useMemo(() => {
    if (fields.length === 0) throw new Error("fields cannot be an empty array");
    return fields || [];
  }, [fields]);

  const {
    rows,
    addRow,
    removeRow,
    clearAllRow,
    changeField,
    changeOperator,
    operatorsForField,
    fieldOptions
  } = useAdvanceSearch({ fields: fieldsData, limitRows });

  const form = useForm<{ [key: string]: string | undefined }>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {}
  });

  const { handleSubmit, unregister, resetField, getValues, clearErrors, setError } = form;

  const [operatorErrors, setOperatorErrors] = React.useState<Record<string, string>>({});
  const clearOperatorError = useCallback(
    (rowId: string) => {
      setOperatorErrors((prev) => {
        if (!(rowId in prev)) return prev;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [rowId]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [setOperatorErrors]
  );

  const clearValue = useCallback(
    (rowId: string, which: "value" | "value2") => {
      const fieldName = `${which}_${rowId}` as `value_${string}` | `value2_${string}`;
      resetField(fieldName, { defaultValue: undefined });
      clearErrors(fieldName);

      const counterpart =
        which === "value" ?
          (`value2_${rowId}` as `value2_${string}`)
        : (`value_${rowId}` as `value_${string}`);
      clearErrors(counterpart);
    },
    [resetField, clearErrors]
  );

  const parseRangeValue = useCallback((raw: string, fieldType: string) => {
    if (!raw) return undefined;
    const normalized = fieldType === "datemonth" ? `${raw}-01` : raw;
    const parsed = parseISO(normalized);
    return isValid(parsed) ? parsed : undefined;
  }, []);

  const onSubmit = useCallback(() => {
    const operatorValidation: Record<string, string> = {};

    rows.forEach((r) => {
      const availableOperators = operatorsForField(r.fieldName);
      if (!availableOperators.length || !availableOperators.includes(r.operator)) {
        operatorValidation[r.id] = "Please select an operator.";
      }
    });

    setOperatorErrors(operatorValidation);
    if (Object.keys(operatorValidation).length > 0) {
      return;
    }

    const currentValues = getValues();
    let hasRangeError = false;

    const rawRows = rows.map((r) => {
      const startFieldName = `value_${r.id}` as `value_${string}`;
      const startValue = currentValues[startFieldName] ?? "";

      if (r.operator === "between") {
        const endFieldName = `value2_${r.id}` as `value2_${string}`;
        const endValue = currentValues[endFieldName] ?? "";

        if (startValue && endValue) {
          const startDate = parseRangeValue(startValue, r.fieldType);
          const endDate = parseRangeValue(endValue, r.fieldType);

          if (startDate && endDate && isAfter(startDate, endDate)) {
            hasRangeError = true;
            setError(startFieldName, {
              type: "validate",
              message: "Start value must be before end value."
            });
            setError(endFieldName, {
              type: "validate",
              message: "End value must be after start value."
            });
          } else {
            clearErrors([startFieldName, endFieldName]);
          }
        }

        return {
          ...r,
          value: startValue ?? "",
          value2: endValue ?? ""
        };
      }

      return {
        ...r,
        value: startValue ?? ""
      };
    });

    if (hasRangeError) {
      return;
    }

    const param = {
      AND: rawRows
        .map((r) => {
          const builder = getBuilder(r.fieldType);
          return builder.build(r);
        })
        .filter(Boolean) as Record<string, unknown>[]
    };

    if (onSearch) {
      onSearch(param);
    }
  }, [
    clearErrors,
    getValues,
    onSearch,
    operatorsForField,
    parseRangeValue,
    rows,
    setError,
    setOperatorErrors
  ]);

  return (
    <ExpandCollapse
      title={<Search className="w-5 h-5" style={{ color: iconColor }} />}
      portalId={portalId}
    >
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="flex justify-center w-full">
          <div className="w-full max-w-4xl rounded-xl bg-white">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold text-gray-700">Select Field</Label>
            </div>
            <div className="space-y-6">
              {rows.map((row, idx) => (
                <AdvanceSearchRow
                  key={row.id}
                  row={row}
                  shortDateFormat={shortDateFormat}
                  isFirst={idx === 0}
                  fields={fieldsData}
                  fieldOptions={fieldOptions}
                  operators={operatorsForField(row.fieldName)}
                  operatorError={operatorErrors[row.id]}
                  onChangeField={(f) => {
                    changeField(row.id, f);
                    clearOperatorError(row.id);
                    clearValue(row.id, "value");
                    clearValue(row.id, "value2");
                  }}
                  onChangeOperator={(op) => {
                    changeOperator(row.id, op);
                    clearOperatorError(row.id);
                    clearValue(row.id, "value");
                    clearValue(row.id, "value2");
                  }}
                  onAdd={addRow}
                  onRemove={() => {
                    clearOperatorError(row.id);
                    removeRow(row.id);
                    unregister(`value_${row.id}`);
                    unregister(`value2_${row.id}`);
                  }}
                  onClearValue={(which) => {
                    clearValue(row.id, which);
                  }}
                  disableAdd={limitRows !== undefined && rows.length >= limitRows}
                />
              ))}
            </div>
            <div className="mt-6 flex flex-col-reverse gap-3 pt-4 md:flex-row md:justify-end">
              <Button
                type="button"
                className="w-full bg-[#8b8b8b] text-white md:w-auto md:min-w-[120px]"
                onClick={() => {
                  clearAllRow();
                  setOperatorErrors({});
                  Object.keys(getValues()).forEach((k) => resetField(k));
                  if (onClear) onClear();
                }}
              >
                Clear Search
              </Button>
              <Button
                type="submit"
                className="w-full bg-[#379a2a] text-white hover:bg-[#2f7c21] md:w-auto md:min-w-[120px]"
              >
                Search
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </ExpandCollapse>
  );
};

export default AdvanceSearch;
